// Stefans-AI-CV-frontend/src/sections/ChatSection.tsx

import { useState, useRef, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import ReactMarkdown from 'react-markdown'
import { useAppStore, useCurrentChat, useAppStatus } from '../stores/appStore'
import { useI18n } from '../i18n'
import { analyticsService } from '../services/analytics'

interface MessageBubbleProps {
  content: string
  isUser: boolean
}

function MessageBubble({ content, isUser }: MessageBubbleProps) {
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}>
      {!isUser && (
        <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-cyan-400/20 to-indigo-400/20 border border-cyan-400/20 flex items-center justify-center flex-shrink-0 mr-2 mt-0.5">
          <span className="text-xs font-mono text-cyan-400 font-bold">S</span>
        </div>
      )}
      <div
        className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm leading-relaxed ${
          isUser
            ? 'bg-cyan-400/10 border border-cyan-400/20 text-text-primary rounded-tr-sm'
            : 'glass-card text-text-secondary rounded-tl-sm'
        }`}
        style={{ wordBreak: 'break-word' }}
      >
        {isUser ? (
          <span style={{ whiteSpace: 'pre-wrap' }}>{content}</span>
        ) : (
          <ReactMarkdown
            components={{
              p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
              strong: ({ children }) => <strong className="text-text-primary font-semibold">{children}</strong>,
              ul: ({ children }) => <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>,
              ol: ({ children }) => <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>,
              li: ({ children }) => <li className="text-text-secondary">{children}</li>,
              code: ({ children }) => <code className="bg-white/5 px-1 py-0.5 rounded text-cyan-400 text-xs font-mono">{children}</code>,
            }}
          >
            {content}
          </ReactMarkdown>
        )}
      </div>
    </div>
  )
}

function TypingDots() {
  return (
    <div className="flex justify-start mb-4">
      <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-cyan-400/20 to-indigo-400/20 border border-cyan-400/20 flex items-center justify-center flex-shrink-0 mr-2 mt-0.5">
        <span className="text-xs font-mono text-cyan-400 font-bold">S</span>
      </div>
      <div className="glass-card px-4 py-3 rounded-2xl rounded-tl-sm flex items-center gap-1.5">
        {[0, 0.2, 0.4].map((delay, i) => (
          <motion.div
            key={i}
            className="w-1.5 h-1.5 rounded-full bg-text-muted"
            animate={{ y: [0, -4, 0], opacity: [0.4, 1, 0.4] }}
            transition={{ duration: 1.2, repeat: Infinity, delay }}
          />
        ))}
      </div>
    </div>
  )
}

export default function ChatSection() {
  const t = useI18n()
  const { chatId, messages } = useCurrentChat()
  const { isTyping } = useAppStatus()
  const { addMessage, setTyping, createNewChat, canSendMessage, currentLanguage, userSession } = useAppStore()

  const [input, setInput] = useState('')
  const [limitWarning, setLimitWarning] = useState<string | null>(null)
  const [streamingText, setStreamingText] = useState<string | null>(null)

  // Ref on the scrollable messages container (not the end sentinel)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  // Scroll ONLY the messages container — never touches the page scroll
  const scrollToBottom = useCallback(() => {
    const el = messagesContainerRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
  }, [])

  // Smart scroll: only auto-scroll if user is already near the bottom
  const isNearBottom = useCallback(() => {
    const el = messagesContainerRef.current
    if (!el) return true
    return el.scrollHeight - el.scrollTop - el.clientHeight < 120
  }, [])

  // Scroll on new messages, typing state, and streaming updates
  useEffect(() => {
    if (isNearBottom()) scrollToBottom()
  }, [messages, isTyping, streamingText, scrollToBottom, isNearBottom])

  const handleSend = async () => {
    const text = input.trim()
    if (!text || isTyping || !chatId) return

    const check = canSendMessage()
    if (!check.canSend) {
      analyticsService.trackEvent('chat_limit_reached', {
        category: 'chat',
        label: check.reason || 'unknown',
        reason: check.reason || 'unknown',
        language: currentLanguage,
      })
      if (check.reason === 'chat_limit') setLimitWarning(t.chat.limit_chat)
      else if (check.reason === 'cookie_limit') setLimitWarning(t.chat.limit_total)
      return
    }

    setLimitWarning(null)
    setInput('')
    analyticsService.trackChatInteraction('message_send')
    analyticsService.trackEvent('chat_message_send', {
      category: 'chat',
      label: currentLanguage,
      language: currentLanguage,
      message_length: text.length,
      visible_message_count: messages.length,
    })
    addMessage({ content: text, isUser: true })
    setTyping(true)
    setStreamingText('')
    // Always scroll when user sends a message
    requestAnimationFrame(() => scrollToBottom())

    try {
      const cookieId = userSession?.cookieId || ''
      const apiBase = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787/api'

      // Send up to 10 previous message pairs as history (20 messages max)
      const history = messages.slice(-20).map((m) => ({
        role: m.isUser ? 'user' : 'assistant',
        content: m.content,
      }))

      const res = await fetch(`${apiBase}/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, chatId, cookieId, language: currentLanguage, history }),
      })

      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`)

      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let accumulated = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          const data = line.slice(6).trim()
          if (!data) continue

          try {
            const parsed = JSON.parse(data) as { type: string; content?: string }
            if (parsed.type === 'content' && parsed.content) {
              accumulated += parsed.content
              setStreamingText(accumulated)
            }
          } catch {
            // skip malformed chunks
          }
        }
      }

      addMessage({ content: accumulated || t.chat.error, isUser: false })
      analyticsService.trackEvent('chat_response_received', {
        category: 'chat',
        label: currentLanguage,
        language: currentLanguage,
        response_length: accumulated.length,
      })
    } catch {
      analyticsService.trackEvent('chat_error', {
        category: 'chat',
        label: currentLanguage,
        language: currentLanguage,
      })
      addMessage({ content: t.chat.error, isUser: false })
    } finally {
      setTyping(false)
      setStreamingText(null)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleNewChat = () => {
    analyticsService.trackEvent('chat_new_chat', {
      category: 'chat',
      label: currentLanguage,
      language: currentLanguage,
    })
    createNewChat()
    setLimitWarning(null)
    setInput('')
  }

  const handleSuggestion = (text: string, index: number) => {
    analyticsService.trackEvent('chat_suggestion_click', {
      category: 'chat',
      label: `suggestion_${index + 1}`,
      language: currentLanguage,
      suggestion_text: text,
    })
    setInput(text)
    inputRef.current?.focus()
  }

  const isEmpty = messages.length === 0

  const whyPoints = [
    { icon: '🔍', text: currentLanguage === 'zh' ? '比简历更真实的双向交流' : 'More genuine than a one-way resume' },
    { icon: '⚡', text: currentLanguage === 'zh' ? '即时获取深度背景信息' : 'Instant access to deep background info' },
    { icon: '💡', text: currentLanguage === 'zh' ? '探索你最感兴趣的角度' : 'Explore from the angle you care about most' },
  ]

  return (
    // 85vh: slightly smaller than full viewport, consistent across resolutions
    <section id="chat" className="h-[85vh] min-h-[520px] flex flex-col relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-bg-elevated/40" />
      <div className="absolute inset-0 grid-pattern opacity-30" />

      <div className="relative z-10 flex flex-col flex-1 w-full max-w-6xl mx-auto px-4 sm:px-6 py-6 md:py-10 min-h-0">
        {/* Section header — fixed height, doesn't grow */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="mb-4 md:mb-6 text-center flex-shrink-0"
        >
          <p className="text-cyan-400 text-sm font-mono mb-1.5">// ai-chat</p>
          <h2 className="font-heading font-bold text-2xl md:text-4xl text-text-primary mb-2">
            {t.chat.title}
          </h2>
          <p className="text-text-secondary max-w-2xl mx-auto text-sm md:text-base">{t.chat.subtitle}</p>
        </motion.div>

        {/* Grid — flex-1 + min-h-0 so it consumes remaining vertical space */}
        <div className="grid md:grid-cols-3 gap-6 flex-1 min-h-0 items-stretch">

          {/* Left: Why panel — sticky, doesn't stretch */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="hidden md:flex flex-col gap-4 self-start sticky top-8"
          >
            <div className="glass-card rounded-2xl p-6">
              <h3 className="text-text-primary font-heading font-semibold text-base mb-4">
                {currentLanguage === 'zh' ? '为什么用AI对话？' : 'Why AI Chat?'}
              </h3>
              <div className="space-y-4">
                {whyPoints.map((point, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className="text-lg flex-shrink-0">{point.icon}</span>
                    <p className="text-text-secondary text-sm leading-relaxed">{point.text}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="glass-card rounded-2xl p-5 border border-cyan-400/10">
              <p className="text-text-muted text-xs font-mono uppercase tracking-wider mb-3">
                {currentLanguage === 'zh' ? '基于真实经历' : 'Based on real experience'}
              </p>
              <div className="space-y-2">
                {['RAG', 'LLM API', 'Vector DB'].map((tech) => (
                  <div key={tech} className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                    <span className="text-text-secondary text-xs font-mono">{tech}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Chat interface — col-span-2, flex col, fills height */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="md:col-span-2 flex flex-col min-h-0"
          >
            {/* Card fills the full column height */}
            <div className="glass-card rounded-2xl overflow-hidden border border-border flex flex-col flex-1 min-h-0">

              {/* Chat header — fixed, never shrinks */}
              <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-border bg-bg-elevated/50">
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-400/30 to-indigo-400/30 border border-cyan-400/30 flex items-center justify-center">
                      <span className="text-xs font-mono text-cyan-400 font-bold">S</span>
                    </div>
                    <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-green-400 border border-bg-base" />
                  </div>
                  <div>
                    <p className="text-text-primary text-sm font-medium">Stefans AI</p>
                    <p className="text-text-muted text-xs">
                      {currentLanguage === 'zh' ? '在线' : 'Online'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleNewChat}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-text-secondary hover:text-text-primary hover:bg-white/5 border border-border hover:border-border-strong transition-all"
                >
                  <span>+</span>
                  <span>{t.chat.new_chat}</span>
                </button>
              </div>

              {/* Messages area — flex-1 + overflow-y-auto = fills space, scrolls internally */}
              <div
                ref={messagesContainerRef}
                className="flex-1 overflow-y-auto p-4 min-h-0"
                style={{ scrollbarWidth: 'thin' }}
              >
                {isEmpty ? (
                  <div className="h-full flex flex-col items-center justify-center text-center">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-cyan-400/20 to-indigo-400/20 border border-cyan-400/20 flex items-center justify-center mb-4">
                      <span className="text-xl font-mono text-cyan-400 font-bold">S</span>
                    </div>
                    <p className="text-text-secondary text-sm mb-6 max-w-xs leading-relaxed">
                      {t.chat.welcome}
                    </p>
                    <div className="flex flex-wrap gap-2 justify-center">
                      {t.chat.suggestions.map((s, i) => (
                        <button
                          key={i}
                          onClick={() => handleSuggestion(s, i)}
                          className="px-3 py-1.5 rounded-xl text-xs text-text-secondary border border-border hover:border-cyan-400/30 hover:text-cyan-400 hover:bg-cyan-dim transition-all duration-200"
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <>
                    {messages.map((msg) => (
                      <MessageBubble key={msg.id} content={msg.content} isUser={msg.isUser} />
                    ))}
                    {/* Streaming bubble — live token-by-token display */}
                    <AnimatePresence>
                      {streamingText !== null && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                          <MessageBubble content={streamingText || '…'} isUser={false} />
                        </motion.div>
                      )}
                    </AnimatePresence>
                    {/* Typing dots — shown only before first token arrives */}
                    <AnimatePresence>
                      {isTyping && streamingText === null && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                          <TypingDots />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </>
                )}
              </div>

              {/* Limit warning — fixed, above input */}
              <AnimatePresence>
                {limitWarning && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="flex-shrink-0 px-4 py-2 bg-amber-500/10 border-t border-amber-500/20"
                  >
                    <p className="text-amber-400 text-xs">{limitWarning}</p>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Input area — fixed at bottom */}
              <div className="flex-shrink-0 border-t border-border p-3">
                <div className="flex items-end gap-2">
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={t.chat.placeholder}
                    rows={1}
                    className="flex-1 bg-bg-elevated/50 border border-border rounded-xl px-3 py-2.5 text-sm text-text-primary placeholder-text-muted resize-none outline-none focus:border-cyan-400/40 transition-colors min-h-[40px] max-h-32"
                    style={{ scrollbarWidth: 'thin' }}
                  />
                  <button
                    onClick={handleSend}
                    disabled={!input.trim() || isTyping}
                    className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200 ${
                      input.trim() && !isTyping
                        ? 'bg-cyan-400 hover:bg-cyan-300 text-bg-base shadow-glow-cyan'
                        : 'bg-white/5 text-text-muted cursor-not-allowed'
                    }`}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="22" y1="2" x2="11" y2="13" />
                      <polygon points="22 2 15 22 11 13 2 9 22 2" />
                    </svg>
                  </button>
                </div>
                <p className="text-text-muted text-xs mt-2 px-1">
                  {currentLanguage === 'zh' ? 'Enter 发送 · Shift+Enter 换行' : 'Enter to send · Shift+Enter for new line'}
                </p>
              </div>

            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
